import { TableGridIcon } from 'components/icons';
import type { Element } from 'hast';
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';
import { NEUTRAL_TILE_CLASS } from 'utils/families';
import { useAiChatTableDownloadTitle, useTableFileRequest } from '../../aiChatTableDownloadContext';
import { textOf } from '../../utils/messageClipboard';
import { buildTableCsv, downloadTable, readTable, tableFileName, typeTable } from '../../utils/tableExport';
import { FigureCopyButton } from './figure/FigureCopyButton';
import { FigureDownload, useFigureDownload } from './figure/FigureDownload';
import { FigureFrame } from './figure/FigureFrame';
import { TABLE_BOX_CLASS_NAME } from './figure/FigureTable';
import TableFileCard from './TableFileCard';

const childrenNamed = (parent: Element | undefined, tagName: string): Element[] =>
    parent?.children.filter((child): child is Element => child.type === 'element' && child.tagName === tagName) ?? [];

interface DownloadProps {
    /** The rendered table, once it is on the page. */
    table: HTMLTableElement | null;
    threadTitle: string;
    rows: number;
    columns: string[];
}

/**
 * The table's Download, with its two formats. When the text block answers a request for a file and this is its first
 * table, the table also draws the card for that file above its heading. The thread title names the file; a thread with
 * none takes the assistant's name.
 */
const TableDownload: React.FC<DownloadProps> = ({ table, threadTitle, rows, columns }) => {
    const intl = useIntl();
    const fileRequest = useTableFileRequest();
    const fallbackTitle = intl.formatMessage({ id: 'ai-chat.title' });
    // The name reads the headings and tables around this one on the page, so it waits for the table to be there.
    const isNamed = table !== null && table.closest('.markdown-prose')?.querySelector('table') === table;
    const cardFileName =
        fileRequest && isNamed ? tableFileName(table, threadTitle, fallbackTitle, fileRequest.format) : null;
    const { generating, download } = useFigureDownload(async (format) => {
        if (table) {
            await downloadTable(table, format, tableFileName(table, threadTitle, fallbackTitle, format), fallbackTitle);
        }
    });

    return (
        <>
            <FigureDownload columns={columns} generating={generating} onDownload={(format) => void download(format)} />
            {fileRequest && cardFileName
                ? createPortal(
                      <TableFileCard
                          format={fileRequest.format}
                          fileName={cardFileName}
                          rows={rows}
                          generating={generating}
                          onDownload={() => void download(fileRequest.format)}
                      />,
                      fileRequest.slot
                  )
                : null}
        </>
    );
};

interface Props {
    /** The table's syntax tree, which says at render how many body rows it has and what its columns are. */
    node?: Element;
    children?: React.ReactNode;
}

/**
 * A markdown table of a chat message, as answers wrote them before Figures: in the table Figure's frame, untitled, its
 * table in the frame's box with "Copy table as CSV" under it. A finished assistant answer's table also offers Download,
 * built from its rendered cells.
 */
const DownloadableTable: React.FC<Props> = ({ node, children }) => {
    const threadTitle = useAiChatTableDownloadTitle();
    const [table, setTable] = useState<HTMLTableElement | null>(null);

    const rows = childrenNamed(childrenNamed(node, 'tbody')[0], 'tr').length;
    const columns = childrenNamed(childrenNamed(childrenNamed(node, 'thead')[0], 'tr')[0], 'th')
        .map((cell) => textOf(cell).trim())
        .filter(Boolean);
    // Its controls and row count read `useIntl`, so they show only with a thread to name the file: tables also render
    // with no IntlProvider.
    const hasDownload = threadTitle !== null && rows > 0;

    return (
        <FigureFrame
            className="my-4"
            icon={TableGridIcon}
            tileClassName={NEUTRAL_TILE_CLASS}
            meta={hasDownload ? <RowCount rows={rows} /> : null}
            actions={
                hasDownload ? (
                    <TableDownload table={table} threadTitle={threadTitle} rows={rows} columns={columns} />
                ) : null
            }
        >
            <div className={TABLE_BOX_CLASS_NAME}>
                <table ref={setTable} className="text-meta w-full border-collapse">
                    {children}
                </table>
            </div>
            {hasDownload ? <CopyTable table={table} /> : null}
        </FigureFrame>
    );
};

const RowCount = ({ rows }: { rows: number }) => {
    const intl = useIntl();
    return <>{intl.formatMessage({ id: 'ai-chat.table.row-count' }, { count: rows })}</>;
};

/** "Copy table as CSV": the text the CSV download holds, read from the rendered table. */
const CopyTable = ({ table }: { table: HTMLTableElement | null }) => {
    const intl = useIntl();
    return (
        <div className="flex items-center gap-1 px-2 pb-1.5">
            <FigureCopyButton
                label={intl.formatMessage({ id: 'ai-chat.figure.copy-table' })}
                copy={() => navigator.clipboard.writeText(table ? buildTableCsv(typeTable(readTable(table))) : '')}
            />
        </div>
    );
};

export default DownloadableTable;
